import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { CUT_TYPES } from '../data/constants';
import { formatPrice } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { PlusIcon, SearchIcon, CheckIcon } from './Icons';
import { Notifications } from '../data/notifications';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';
import PrintLabelModal from './PrintLabelModal';

// ── Constants ────────────────────────────────────────────────
const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'planning', label: 'Planning' },
  { id: 'pending_approval', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'quality_check', label: 'QC' },
  { id: 'completed', label: 'Completed' },
];

const STATUS_COLORS = {
  planning: { bg: 'rgba(88,166,255,0.12)', color: 'var(--blue)', label: 'Planning' },
  pending_customer_confirmation: { bg: 'rgba(210,153,34,0.12)', color: 'var(--yellow)', label: 'Awaiting Customer' },
  customer_confirmed: { bg: 'rgba(63,185,80,0.12)', color: 'var(--green)', label: 'Customer Confirmed' },
  pending_approval: { bg: 'rgba(210,153,34,0.12)', color: 'var(--yellow)', label: 'Pending Approval' },
  approved: { bg: 'rgba(63,185,80,0.12)', color: 'var(--green)', label: 'Approved' },
  in_progress: { bg: 'rgba(88,166,255,0.12)', color: 'var(--blue)', label: 'In Progress' },
  quality_check: { bg: 'rgba(188,140,255,0.12)', color: '#bc8cff', label: 'Quality Check' },
  completed: { bg: 'rgba(63,185,80,0.12)', color: 'var(--green)', label: 'Completed' },
  rejected: { bg: 'rgba(248,81,73,0.12)', color: 'var(--red)', label: 'Rejected' },
  cancelled: { bg: 'rgba(248,81,73,0.12)', color: 'var(--red)', label: 'Cancelled' },
  reverted: { bg: 'rgba(139,141,148,0.12)', color: 'var(--text3)', label: 'Reverted' },
};

const ALL_CUT_TYPES = ['Half Cut', 'Nose Cut', 'Rear Cut', 'Front Cut', 'CKD', 'SKD', 'Roof Cut', 'Engine Only'];

const ALL_PARTS = {
  'Engine & Drivetrain': [
    { id: 'engine', label: 'Engine' }, { id: 'transmission', label: 'Transmission' },
    { id: 'transfer_case', label: 'Transfer case' }, { id: 'front_differential', label: 'Front differential' },
    { id: 'rear_differential', label: 'Rear differential' }, { id: 'propeller_shaft', label: 'Propeller shaft' },
  ],
  'Front Body': [
    { id: 'front_bumper', label: 'Front bumper' }, { id: 'hood', label: 'Hood / bonnet' },
    { id: 'front_fenders', label: 'Front fenders' }, { id: 'front_lights', label: 'Front lights' },
    { id: 'grille', label: 'Grille' }, { id: 'radiator', label: 'Radiator' },
    { id: 'ac_condenser', label: 'AC condenser' }, { id: 'ac_compressor', label: 'AC compressor' },
  ],
  'Rear Body': [
    { id: 'rear_bumper', label: 'Rear bumper' }, { id: 'rear_lights', label: 'Rear lights' },
    { id: 'tailgate', label: 'Tailgate' }, { id: 'rear_fenders', label: 'Rear fenders' },
    { id: 'rear_body_shell', label: 'Rear body shell' },
  ],
  'Interior': [
    { id: 'dashboard', label: 'Dashboard' }, { id: 'steering_column', label: 'Steering column' },
    { id: 'steering_rack', label: 'Steering rack' }, { id: 'front_seats', label: 'Front seats' },
    { id: 'rear_seats', label: 'Rear seats' }, { id: 'center_console', label: 'Center console' },
    { id: 'wiring_harness', label: 'Wiring harness' },
  ],
  'Suspension': [
    { id: 'front_suspension', label: 'Front suspension' }, { id: 'front_shocks', label: 'Front shocks' },
    { id: 'rear_suspension', label: 'Rear suspension' }, { id: 'rear_shocks', label: 'Rear shocks' },
  ],
  'Doors': [
    { id: 'front_doors', label: 'Front doors' }, { id: 'rear_doors', label: 'Rear doors' },
  ],
  'Exhaust & Other': [
    { id: 'catalyst', label: 'With catalyst' }, { id: 'exhaust_system', label: 'Exhaust system' },
    { id: 'fuel_tank', label: 'Fuel tank' },
  ],
};
const ALL_PART_IDS = Object.values(ALL_PARTS).flat().map(p => p.id);
const getPartLabel = (id) => Object.values(ALL_PARTS).flat().find(p => p.id === id)?.label || id.replace(/_/g, ' ');

function timeAgo(ts) {
  if (!ts) return '';
  const d = Date.now() - new Date(ts).getTime();
  if (d < 60000) return 'just now';
  if (d < 3600000) return `${Math.floor(d / 60000)}m ago`;
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`;
  return `${Math.floor(d / 86400000)}d ago`;
}

async function compressAndUpload(file) {
  if (!file) return '';
  const compressed = await new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
  return await uploadImage(compressed) || '';
}

async function savePlan(plan) {
  const all = DB.get('cutPlans') || [];
  const exists = all.find(p => p.id === plan.id);
  let result;
  if (exists) {
    const merged = { ...exists, ...plan, _supaId: plan._supaId || exists._supaId };
    result = await DB.setAsync('cutPlans', all.map(p => p.id === plan.id ? merged : p));
  } else {
    result = await DB.setAsync('cutPlans', [...all, plan]);
  }
  if (result?.error) { console.error('[savePlan] Failed:', result.error); alert('Failed to save cut plan. Please try again.'); }
  return result;
}

// ═══════════════════════════════════════════════════════════════
// MAIN CUT PLANS PAGE
// ═══════════════════════════════════════════════════════════════
export default function CutPlans({ user, setPage }) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [activePlan, setActivePlan] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [, forceUpdate] = useState(0);

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const plans = (DB.get('cutPlans') || []).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const templates = DB.get('cutTemplates') || [];

  let filtered = plans;
  if (tab !== 'all') filtered = filtered.filter(p => p.status === tab);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      [p.vehicle_name, p.vehicle_sku, p.cut_type, p.customer_name, p.assigned_to_name, `CP-${p.plan_number}`]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }

  const counts = {};
  plans.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });

  if (activePlan) {
    return <CutPlanDetail plan={activePlan} user={user} templates={templates}
      onBack={() => { setActivePlan(null); forceUpdate(v => v + 1); }}
      onUpdate={async (updated) => { await savePlan(updated); setActivePlan(updated); forceUpdate(v => v + 1); }}
      setPage={setPage} />;
  }

  if (showNewForm) {
    return <NewCutPlanForm user={user} templates={templates} onBack={() => setShowNewForm(false)}
      onCreated={async (plan) => { await savePlan(plan); setShowNewForm(false); setActivePlan(plan); forceUpdate(v => v + 1); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Cut Plans</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setTab('analytics')}>📊 Analytics</button>
          <button className="btn btn-primary" onClick={() => setShowNewForm(true)}><PlusIcon /> New Cut Plan</button>
        </div>
      </div>

      {tab === 'analytics' && <CutPlanAnalytics plans={plans} onBack={() => setTab('all')} />}

      <div className="tabs" style={{ marginBottom: 14 }}>
        {STATUS_TABS.map(t => (
          <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}<span className="cnt">{t.id === 'all' ? plans.length : (counts[t.id] || 0)}</span>
          </button>
        ))}
      </div>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}><SearchIcon /></span>
        <input className="units-search" style={{ paddingLeft: 36, width: '100%', boxSizing: 'border-box' }}
          placeholder="Search vehicle, SKU, customer, type..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✂️</div>
          <div style={{ fontSize: 14 }}>{search ? 'No plans match' : 'No cut plans yet'}</div>
        </div>
      ) : filtered.map(plan => {
        const st = STATUS_COLORS[plan.status] || STATUS_COLORS.planning;
        const msgs = (plan.discussion || []).length;
        const photos = (plan.photos_before || []).length;
        return (
          <div key={plan.id} className="card" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => setActivePlan(plan)}>
            <div className="card-body" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14 }}>✂️</span>
                    <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--cyan)' }}>#CP-{String(plan.plan_number || '').padStart(3, '0')}</span>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{plan.vehicle_name || 'Unknown'}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase' }}>{plan.cut_type}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {plan.vehicle_sku}{plan.initiated_by === 'customer_request' ? ` · Customer: ${plan.customer_name || '—'}` : ' · Business decision'}
                  </div>
                </div>
                <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>
              </div>
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
                {plan.assigned_to_name && <span>Assigned: {plan.assigned_to_name}</span>}
                {plan.started_at && <span>Started: {timeAgo(plan.started_at)}</span>}
                {plan.estimated_days && <span>Est: {plan.estimated_days}d</span>}
                {msgs > 0 && <span>💬 {msgs}</span>}
                {photos > 0 && <span>📷 {photos}</span>}
                {plan.total_parts_created > 0 && <span>🔧 {plan.total_parts_created} parts</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// NEW CUT PLAN FORM
// ═══════════════════════════════════════════════════════════════
function NewCutPlanForm({ user, templates, onBack, onCreated }) {
  const [reason, setReason] = useState('business_decision');
  const [customerId, setCustomerId] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [cutType, setCutType] = useState('');
  const [customCutType, setCustomCutType] = useState('');
  const [included, setIncluded] = useState({});
  const [additional, setAdditional] = useState('');
  const [cuttingFee, setCuttingFee] = useState('');
  const [cuttingCurrency, setCuttingCurrency] = useState('USD');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [mainCostPct, setMainCostPct] = useState(60);
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const customers = DB.get('customers') || [];
  const users = DB.get('users') || [];
  const vehicles = DB.get('vehicles') || [];
  const spares = DB.get('spareParts') || [];

  const searchResults = vehicleSearch.length >= 2 ? [
    ...vehicles.filter(v => !['sold', 'dismantled', 'converted', 'sold_awaiting_payment'].includes(v.status))
      .filter(v => [v.sku, v.make, v.model, v.chassis].filter(Boolean).join(' ').toLowerCase().includes(vehicleSearch.toLowerCase()))
      .slice(0, 8).map(v => ({ ...v, _srcType: 'vehicle' })),
    ...spares.filter(p => !['sold', 'dismantled'].includes(p.status) && (p.category || '').match(/rear section|front section|body shell|ckd|skd|half cut|nose cut/i))
      .filter(p => [p.sku, p.partName, p.make, p.model].filter(Boolean).join(' ').toLowerCase().includes(vehicleSearch.toLowerCase()))
      .slice(0, 4).map(p => ({ ...p, _srcType: 'part' })),
  ] : [];

  const remainingParts = ALL_PART_IDS.filter(id => !included[id]).map(id => getPartLabel(id));

  const applyTemplate = (tmplId) => {
    setSelectedTemplate(tmplId);
    const tmpl = templates.find(t => t.id === tmplId);
    if (!tmpl) return;
    setCutType(tmpl.cut_type);
    setIncluded(typeof tmpl.included_parts === 'object' ? tmpl.included_parts : {});
  };

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';

  const handleCreate = (asDraft) => {
    if (!selectedVehicle) { alert('Select a source vehicle.'); return; }
    const finalCutType = cutType || customCutType;
    if (!finalCutType) { alert('Select a cut type.'); return; }

    const assignedUser = users.find(u => u.id === assignedTo);
    const customer = customers.find(c => c.id === customerId);

    const plan = {
      id: DB.genId('CP'),
      plan_number: (DB.get('cutPlans') || []).length + 1,
      vehicle_id: selectedVehicle.id,
      vehicle_sku: selectedVehicle.sku,
      vehicle_name: `${selectedVehicle.year || ''} ${selectedVehicle.make || ''} ${selectedVehicle.model || ''}`.trim() || selectedVehicle.partName || selectedVehicle.sku,
      initiated_by: reason,
      customer_id: reason === 'customer_request' ? customerId : null,
      customer_name: reason === 'customer_request' ? (customer?.name || '') : null,
      cut_type: finalCutType,
      custom_cut_type: customCutType || null,
      template_id: selectedTemplate || null,
      included_parts: included,
      remaining_parts: remainingParts,
      scrap_parts: [],
      additional_notes: [notes, additional ? `Additional: ${additional}` : ''].filter(Boolean).join('\n'),
      cutting_fee: Number(cuttingFee) || 0,
      cutting_currency: cuttingCurrency,
      estimated_days: Number(estimatedDays) || null,
      parent_purchase_price: selectedVehicle.costJPY || selectedVehicle.purchasePrice || 0,
      parent_purchase_currency: 'JPY',
      main_part_cost_percent: mainCostPct,
      discussion: [],
      photos_before: [], photos_during: [], photos_after: [],
      status: asDraft ? 'planning' : 'pending_approval',
      assigned_to: assignedTo || null,
      assigned_to_name: assignedUser?.name || null,
      created_by: user.id,
      created_by_name: user.name,
      current_step: 0, step_data: {},
      created_at: new Date().toISOString(),
    };
    onCreated(plan);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <h2 style={{ margin: 0 }}>New Cut Plan</h2>
      </div>

      {/* Why */}
      <Section title="Why are we cutting?">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[{ id: 'business_decision', label: 'Business Decision' }, { id: 'customer_request', label: 'Customer Request' }].map(r => (
            <button key={r.id} onClick={() => setReason(r.id)} className={`btn btn-sm ${reason === r.id ? 'btn-primary' : 'btn-secondary'}`}>
              {reason === r.id ? '● ' : '○ '}{r.label}
            </button>
          ))}
        </div>
        {reason === 'customer_request' && (
          <select className="form-select" value={customerId} onChange={e => setCustomerId(e.target.value)}>
            <option value="">Select customer...</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name} — {c.company || c.country}</option>)}
          </select>
        )}
      </Section>

      {/* Source Vehicle */}
      <Section title="Source Vehicle">
        <input className="form-input" placeholder="Search by SKU, chassis, make..." value={vehicleSearch}
          onChange={e => { setVehicleSearch(e.target.value); setSelectedVehicle(null); }} />
        {searchResults.length > 0 && !selectedVehicle && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
            {searchResults.map(v => (
              <div key={v.id} onClick={() => { setSelectedVehicle(v); setVehicleSearch(''); }}
                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <div style={{ fontWeight: 600 }}>{v._srcType === 'part' ? '🔧' : '🚗'} {v.make} {v.model} {v.year || ''}</div>
                <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{v.sku}</div>
              </div>
            ))}
          </div>
        )}
        {selectedVehicle && (
          <div style={{ marginTop: 10, padding: 12, background: 'var(--bg3)', borderRadius: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
            {selectedVehicle.images?.[0] && <img src={selectedVehicle.images[0]} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{selectedVehicle._srcType === 'part' ? '🔧' : '🚗'} {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year || ''}</div>
              <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{selectedVehicle.sku}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {[selectedVehicle.grade ? `Grade ${selectedVehicle.grade}` : '', selectedVehicle.mileage ? `${Number(selectedVehicle.mileage).toLocaleString()} km` : '', selectedVehicle.fuel || selectedVehicle.fuelType, selectedVehicle.yard_location].filter(Boolean).join(' · ')}
              </div>
              {(selectedVehicle.costJPY || selectedVehicle.purchasePrice) > 0 && (
                <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>Purchase: ¥{(selectedVehicle.costJPY || selectedVehicle.purchasePrice).toLocaleString()}</div>
              )}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedVehicle(null)}>✕</button>
          </div>
        )}
      </Section>

      {/* Cut Type */}
      <Section title="Cut Type">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {ALL_CUT_TYPES.map(ct => (
            <button key={ct} onClick={() => { setCutType(ct); setCustomCutType(''); }}
              className={`btn btn-sm ${cutType === ct ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 11 }}>{ct}</button>
          ))}
          <input className="form-input" style={{ width: 140, fontSize: 11, padding: '4px 8px' }}
            placeholder="+ Custom..." value={customCutType}
            onChange={e => { setCustomCutType(e.target.value); setCutType(''); }} onFocus={() => setCutType('')} />
        </div>
        {templates.length > 0 && (
          <select className="form-select" style={{ fontSize: 11 }} value={selectedTemplate} onChange={e => applyTemplate(e.target.value)}>
            <option value="">Load template...</option>
            {templates.filter(t => !cutType || t.cut_type === cutType).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </Section>

      {/* Included parts */}
      <Section title="What's included in the main cut">
        {Object.entries(ALL_PARTS).map(([group, parts]) => (
          <div key={group} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>{group}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 4 }}>
              {parts.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!included[p.id]} onChange={e => setIncluded(prev => ({ ...prev, [p.id]: e.target.checked }))} style={{ accentColor: 'var(--green)' }} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
        ))}
        <input className="form-input" placeholder="Additional items (e.g. AW tires, roof rack)" value={additional} onChange={e => setAdditional(e.target.value)} />
      </Section>

      {/* Remaining */}
      {remainingParts.length > 0 && (
        <Section title="Remaining parts (auto-calculated)">
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            {remainingParts.map((p, i) => <span key={i} style={{ display: 'inline-block', padding: '2px 8px', margin: '2px 4px 2px 0', background: 'var(--bg3)', borderRadius: 4, fontSize: 11 }}>{p}</span>)}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>These become separate inventory after cutting.</div>
        </Section>
      )}

      {/* Charges */}
      <Section title="Charges & Cost Allocation">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Cutting fee</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="form-input" type="number" style={{ width: 100 }} value={cuttingFee} onChange={e => setCuttingFee(e.target.value)} />
              <select className="form-select" style={{ width: 70 }} value={cuttingCurrency} onChange={e => setCuttingCurrency(e.target.value)}>
                <option>USD</option><option>AED</option><option>JPY</option>
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Est. days</div>
            <input className="form-input" type="number" style={{ width: 60 }} value={estimatedDays} onChange={e => setEstimatedDays(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Main cut gets %</div>
            <input className="form-input" type="number" style={{ width: 60 }} value={mainCostPct} onChange={e => setMainCostPct(Number(e.target.value))} />
          </div>
        </div>
      </Section>

      {/* Assign + Notes */}
      <Section title="Assignment & Notes">
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Assign to</div>
          <select className="form-select" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
            <option value="">Not assigned</option>
            {users.filter(u => u.role === 'japan_yard' || u.role === 'admin').map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>Notes for yard team</div>
          <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Clean cut at B-pillar. Dashboard must stay intact." style={{ resize: 'vertical' }} />
        </div>
      </Section>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onBack}>Cancel</button>
        <button className="btn btn-secondary" onClick={() => handleCreate(true)}>Save as Draft</button>
        <button className="btn btn-primary" onClick={() => handleCreate(false)}>
          {isAdmin ? 'Create & Start Discussion' : 'Submit for Approval'}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CUT PLAN DETAIL — Discussion + Approval + Progressive Cutting
// ═══════════════════════════════════════════════════════════════
function CutPlanDetail({ plan, user, templates, onBack, onUpdate, setPage }) {
  const [message, setMessage] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [printUnit, setPrintUnit] = useState(null);
  const [msgPhotos, setMsgPhotos] = useState([]);
  const msgPhotoRef = useRef(null);

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const isAssigned = plan.assigned_to === user.id;
  const canEdit = isAdmin || isAssigned;
  const st = STATUS_COLORS[plan.status] || STATUS_COLORS.planning;
  const vehicle = [...(DB.get('vehicles') || []), ...(DB.get('spareParts') || [])].find(v => v.id === plan.vehicle_id);
  const stepData = plan.step_data || {};

  // Discussion
  const sendMessage = async () => {
    const text = message.trim();
    if (!text && msgPhotos.length === 0) return;
    const msg = { id: DB.genId('MSG'), user_id: user.id, user_name: user.name, user_role: user.role, message: text, photos: msgPhotos, created_at: new Date().toISOString() };
    onUpdate({ ...plan, discussion: [...(plan.discussion || []), msg] });
    setMessage(''); setMsgPhotos([]);
  };

  // Approval
  const handleApprove = () => {
    if (!window.confirm('Approve this cut plan?')) return;
    const updated = { ...plan, status: 'approved', approved_by: user.id, approved_by_name: user.name, approved_at: new Date().toISOString() };
    if (vehicle) centralUpdateStatus(plan.vehicle_id, detectUnitType(vehicle), 'cutting', { updatedBy: user.name });
    Notifications.add('cut_plan_approved', 'Cut Plan Approved', `#CP-${plan.plan_number} approved for ${plan.vehicle_name}`);
    onUpdate(updated);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) { alert('Enter a reason.'); return; }
    onUpdate({ ...plan, status: 'rejected', rejection_reason: rejectReason.trim() });
    setShowReject(false);
  };

  // Step handlers
  const handleBeforePhoto = async (file) => {
    const url = await compressAndUpload(file);
    if (url) onUpdate({ ...plan, photos_before: [...(plan.photos_before || []), url] });
  };

  const handleStartCutting = () => {
    if ((plan.photos_before || []).length < 2) { alert('Upload at least 2 before photos.'); return; }
    onUpdate({ ...plan, status: 'in_progress', started_at: new Date().toISOString(), current_step: 2 });
    Notifications.add('general', 'Cutting Started', `${user.name} started cutting ${plan.vehicle_name}`);
  };

  const handleMainCutPhoto = async (file) => {
    const url = await compressAndUpload(file);
    if (url) onUpdate({ ...plan, step_data: { ...stepData, mainPhotos: [...(stepData.mainPhotos || []), url] } });
  };

  const createPart = async (partName, photos, conditionNote, costJPY) => {
    const partsList = DB.get('spareParts') || [];
    const makeCode = (vehicle?.make || 'UNK').substring(0, 3).toUpperCase();
    const d = new Date();
    const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
    const newSku = `PRT-${makeCode}-${dateCode}-${String(partsList.length + 1).padStart(3, '0')}`;
    const newId = DB.genId('PRT');
    const newPart = {
      id: newId, sku: newSku,
      partName: `${partName} — ${plan.vehicle_name}`,
      category: partName, condition: conditionNote || 'Used',
      make: vehicle?.make, model: vehicle?.model, year: vehicle?.year,
      chassis: vehicle?.chassis, images: photos || [],
      costJPY: costJPY || 0, status: 'available', quantity: 1,
      yard_location: vehicle?.yard_location || 'sharjah_yard',
      parent_unit_id: plan.vehicle_id, parent_unit_sku: plan.vehicle_sku,
      parent_unit_type: 'vehicle', cut_level: 1, cut_plan_id: plan.id,
      root_vehicle_id: plan.vehicle_id, root_vehicle_sku: plan.vehicle_sku,
      created_from_cut: true, createdAt: new Date().toISOString(), daysInStock: 0,
    };
    const result = await DB.setAsync('spareParts', [...partsList, newPart]);
    if (result?.error) { console.error('[createPart] Failed:', result.error); alert('Failed to save new part. Please try again.'); return null; }
    return { id: newId, sku: newSku };
  };

  const handleSaveMainCut = async () => {
    const mainPhotos = stepData.mainPhotos || [];
    if (mainPhotos.length === 0) { alert('Take at least 1 photo.'); return; }
    const mainCost = plan.parent_purchase_price ? Math.round(plan.parent_purchase_price * (plan.main_part_cost_percent / 100)) : 0;
    const created = await createPart(plan.cut_type, mainPhotos, stepData.mainCondition, mainCost);
    if (!created) return;
    const { id, sku } = created;

    if (plan.customer_id) {
      const parts = DB.get('spareParts') || [];
      const assignResult = await DB.setAsync('spareParts', parts.map(p => p.id === id ? { ...p, assignedTo: plan.customer_id, status: 'assigned' } : p));
      if (assignResult?.error) { console.error('[handleSaveMainCut] Customer assign failed:', assignResult.error); alert('Part created but customer assignment failed. Please assign manually.'); }
    }

    onUpdate({ ...plan, current_step: 3, main_part_id: id, main_part_sku: sku, child_part_ids: [id], child_part_skus: [sku], total_parts_created: 1, step_data: { ...stepData, mainPartId: id, mainPartSku: sku } });
  };

  const completePlan = async (ids, skus) => {
    if (vehicle) {
      centralUpdateStatus(plan.vehicle_id, detectUnitType(vehicle), 'dismantled', { updatedBy: user.name });
      const vList = DB.get('vehicles') || [];
      const vResult = await DB.setAsync('vehicles', vList.map(v => v.id === plan.vehicle_id ? { ...v, child_part_ids: ids, child_part_skus: skus, dismantled_at: new Date().toISOString(), dismantled_into: plan.cut_type, cut_plan_id: plan.id } : v));
      if (vResult?.error) { console.error('[completePlan] Vehicle update failed:', vResult.error); alert('Cut plan completed but vehicle record failed to update. Please check the vehicle manually.'); }
    }
    Notifications.add('general', 'Cut Plan Complete', `#CP-${plan.plan_number} completed. ${ids.length} parts from ${plan.vehicle_name}.`);
    onUpdate({ ...plan, status: 'completed', completed_at: new Date().toISOString(), completed_by: user.id, completed_by_name: user.name, child_part_ids: ids, child_part_skus: skus, total_parts_created: ids.length, current_step: 100 });
  };

  const handleKeepAsOne = async () => {
    const remainCost = plan.parent_purchase_price ? Math.round(plan.parent_purchase_price * ((100 - plan.main_part_cost_percent) / 100)) : 0;
    const created = await createPart(`Remaining Section`, vehicle?.images?.slice(0, 1) || [], 'Used', remainCost);
    if (!created) return;
    const { id, sku } = created;
    await completePlan([...(plan.child_part_ids || []), id], [...(plan.child_part_skus || []), sku]);
  };

  const handleDismantleNow = () => { onUpdate({ ...plan, current_step: 4, step_data: { ...stepData, dismantleIndex: 0 } }); };
  const handleDoneForNow = () => { onUpdate({ ...plan, current_step: 99 }); };

  const remainingList = (plan.remaining_parts || []).filter(Boolean);
  const dismantleIdx = stepData.dismantleIndex || 0;

  const handleDismantlePart = async (partName, photos, conditionNote, skip) => {
    const newIds = [...(plan.child_part_ids || [])];
    const newSkus = [...(plan.child_part_skus || [])];
    if (!skip) {
      const perCost = plan.parent_purchase_price ? Math.round((plan.parent_purchase_price * ((100 - plan.main_part_cost_percent) / 100)) / remainingList.length) : 0;
      const created = await createPart(partName, photos, conditionNote, perCost);
      if (!created) return;
      newIds.push(created.id); newSkus.push(created.sku);
    }
    const nextIdx = dismantleIdx + 1;
    if (nextIdx >= remainingList.length) {
      await completePlan(newIds, newSkus);
    } else {
      onUpdate({ ...plan, child_part_ids: newIds, child_part_skus: newSkus, total_parts_created: newIds.length, step_data: { ...stepData, dismantleIndex: nextIdx } });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <h2 style={{ margin: 0, flex: 1 }}>✂️ #CP-{String(plan.plan_number || '').padStart(3, '0')}</h2>
        <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}>{st.label}</span>
      </div>

      {/* Vehicle info */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
          {vehicle?.images?.[0] && <img src={vehicle.images[0]} alt="" style={{ width: 70, height: 50, objectFit: 'cover', borderRadius: 6 }} />}
          <div>
            <div style={{ fontWeight: 700 }}>{plan.vehicle_name}</div>
            <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{plan.vehicle_sku}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {plan.cut_type} · {plan.initiated_by === 'customer_request' ? `Customer: ${plan.customer_name}` : 'Business decision'}
              {plan.assigned_to_name && ` · Assigned: ${plan.assigned_to_name}`}
            </div>
          </div>
        </div>
      </div>

      {/* Progressive cutting steps */}
      {plan.status === 'approved' && plan.current_step < 2 && canEdit && (
        <StepCard title="Step 1: Before Photos" step={1} total={4}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Take photos of the full car BEFORE cutting.</div>
          <PhotoUploader photos={plan.photos_before || []} onUpload={handleBeforePhoto} />
          <div style={{ fontSize: 11, color: (plan.photos_before || []).length >= 2 ? 'var(--green)' : 'var(--yellow)', margin: '8px 0' }}>
            {(plan.photos_before || []).length >= 2 ? '✅' : '⚠️'} {(plan.photos_before || []).length} photos — min 2
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={(plan.photos_before || []).length < 2} onClick={handleStartCutting}>▶️ Start Cutting</button>
        </StepCard>
      )}

      {plan.status === 'in_progress' && plan.current_step === 2 && canEdit && (
        <StepCard title={`Step 2: Main Cut — ${plan.cut_type}`} step={2} total={4}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Cut the {plan.cut_type}, print QR, stick it, then photograph.</div>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <button className="btn btn-secondary" onClick={() => setPrintUnit({ sku: stepData.mainPartSku || 'PRT-PREVIEW', make: vehicle?.make, model: vehicle?.model, year: vehicle?.year, chassis: vehicle?.chassis, color: vehicle?.color })}>🖨️ Print QR Label</button>
          </div>
          <PhotoUploader photos={stepData.mainPhotos || []} onUpload={handleMainCutPhoto} label="Photos with QR visible" />
          <div style={{ padding: 8, background: 'var(--bg3)', borderRadius: 8, margin: '8px 0', fontSize: 11, lineHeight: 1.8 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Included:</div>
            {Object.entries(plan.included_parts || {}).filter(([, v]) => v).map(([id]) => (
              <span key={id} style={{ display: 'inline-block', padding: '1px 6px', margin: 2, background: 'var(--bg1)', borderRadius: 4 }}>✅ {getPartLabel(id)}</span>
            ))}
          </div>
          <input className="form-input" style={{ marginBottom: 8 }} placeholder="Condition notes..." value={stepData.mainCondition || ''}
            onChange={e => onUpdate({ ...plan, step_data: { ...stepData, mainCondition: e.target.value } })} />
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={(stepData.mainPhotos || []).length === 0} onClick={handleSaveMainCut}>
            Save {plan.cut_type} & Next →
          </button>
        </StepCard>
      )}

      {plan.status === 'in_progress' && plan.current_step === 3 && canEdit && (
        <StepCard title="Step 3: What's Remaining?" step={3} total={4}>
          <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, marginBottom: 8 }}>✅ {plan.cut_type} created: {plan.main_part_sku}</div>
          <div style={{ marginBottom: 12 }}>
            {remainingList.map((p, i) => <div key={i} style={{ fontSize: 12, padding: '3px 0' }}>☑ {p}</div>)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={handleKeepAsOne}>📦 Keep as One Piece</button>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleDismantleNow}>✂️ Separate Each Part ({remainingList.length})</button>
            <button className="btn btn-secondary" style={{ width: '100%', opacity: 0.6 }} onClick={handleDoneForNow}>⏸️ Done for Now</button>
          </div>
        </StepCard>
      )}

      {plan.status === 'in_progress' && plan.current_step === 4 && canEdit && dismantleIdx < remainingList.length && (
        <DismantleStep partName={remainingList[dismantleIdx]} index={dismantleIdx} total={remainingList.length}
          vehicle={vehicle} onDone={handleDismantlePart} setPrintUnit={setPrintUnit} />
      )}

      {plan.status === 'completed' && <CompletionSummary plan={plan} />}

      {/* Revert button for completed plans — admin only */}
      {plan.status === 'completed' && isAdmin && (
        <Section title="Revert Cut Plan">
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>This will delete all created parts and restore the vehicle to available. Only possible if no parts have been sold.</div>
          <button className="btn btn-secondary" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={async () => {
            const allParts = DB.get('spareParts') || [];
            const childIds = plan.child_part_ids || [];
            const children = allParts.filter(p => childIds.includes(p.id));
            const sold = children.filter(c => ['sold', 'sold_awaiting_payment', 'shipped', 'delivered'].includes(c.status));
            const inContainer = children.filter(c => {
              const containers = DB.get('containers') || [];
              return containers.some(ct => (ct.units || []).includes(c.id));
            });

            if (sold.length > 0) {
              alert(`Cannot revert — ${sold.length} part${sold.length !== 1 ? 's have' : ' has'} been sold:\n${sold.map(s => s.sku).join(', ')}`);
              return;
            }
            if (inContainer.length > 0) {
              alert(`Cannot revert — ${inContainer.length} part${inContainer.length !== 1 ? 's are' : ' is'} in a container:\n${inContainer.map(s => s.sku).join(', ')}`);
              return;
            }

            if (!window.confirm(`Revert cut plan #CP-${plan.plan_number}?\n\nThis will:\n- Delete ${children.length} created parts\n- Restore vehicle ${plan.vehicle_sku} to available\n- Mark this plan as reverted\n\nThis cannot be undone.`)) return;

            const delResult = await DB.setAsync('spareParts', allParts.filter(p => !childIds.includes(p.id)));
            if (delResult?.error) { console.error('[revert] Failed to delete parts:', delResult.error); alert('Failed to delete child parts. Revert aborted.'); return; }
            childIds.forEach(id => DB.deleteRecord('spareParts', id));

            const vehicle = (DB.get('vehicles') || []).find(v => v.id === plan.vehicle_id);
            if (vehicle) {
              centralUpdateStatus(plan.vehicle_id, 'vehicle', 'available', {
                updatedBy: user.name,
                extraFields: { child_part_ids: [], child_part_skus: [], dismantled_at: null, dismantled_into: null, cut_plan_id: null }
              });
            }

            onUpdate({ ...plan, status: 'reverted' });
            Notifications.add('general', 'Cut Plan Reverted', `#CP-${plan.plan_number} reverted. ${children.length} parts deleted, vehicle restored.`);
            alert('✅ Cut plan reverted. Vehicle restored to available.');
          }}>
            🔄 Revert — Delete All Parts & Restore Vehicle
          </button>
        </Section>
      )}

      {/* Customer cancellation window */}
      {plan.status === 'pending_customer_confirmation' && plan.customer_id && (
        <Section title="Customer Confirmation">
          {(() => {
            const deadline = plan.confirmation_deadline ? new Date(plan.confirmation_deadline) : null;
            const now = new Date();
            const canCancel = deadline ? now < deadline : true;
            const timeLeft = deadline ? Math.max(0, Math.round((deadline - now) / 60000)) : null;
            return (
              <>
                <div style={{ fontSize: 12, marginBottom: 8 }}>
                  Quote: <strong>{plan.cutting_currency} {plan.cutting_fee?.toLocaleString()}</strong> cutting fee
                </div>
                {timeLeft !== null && (
                  <div style={{ fontSize: 11, color: canCancel ? 'var(--yellow)' : 'var(--green)', marginBottom: 8 }}>
                    {canCancel ? `⏱️ ${timeLeft} minutes left to cancel` : '✅ Cancellation window closed — customer is committed'}
                  </div>
                )}
                {canCancel && isAdmin && (
                  <button className="btn btn-secondary" style={{ color: 'var(--red)' }} onClick={() => {
                    if (!window.confirm('Cancel this cut plan?')) return;
                    onUpdate({ ...plan, status: 'cancelled' });
                  }}>Cancel Cut Plan</button>
                )}
              </>
            );
          })()}
        </Section>
      )}

      {plan.current_step === 99 && plan.status === 'in_progress' && canEdit && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-body" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>⏸️ Paused — main cut done, remaining parts pending</div>
            <button className="btn btn-primary" onClick={() => onUpdate({ ...plan, current_step: 3 })}>Resume</button>
          </div>
        </div>
      )}

      {/* Approval */}
      {['planning', 'pending_approval', 'customer_confirmed'].includes(plan.status) && isAdmin && (
        <Section title="Decision">
          {!showReject ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success" style={{ flex: 1 }} onClick={handleApprove}><CheckIcon /> Approve & Assign</button>
              <button className="btn btn-secondary" style={{ color: 'var(--red)' }} onClick={() => setShowReject(true)}>Reject</button>
            </div>
          ) : (
            <div>
              <textarea className="form-input" rows={2} placeholder="Rejection reason..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setShowReject(false)}>Cancel</button>
                <button className="btn btn-primary" style={{ background: 'var(--red)', flex: 1 }} onClick={handleReject}>Confirm Rejection</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {plan.status === 'rejected' && plan.rejection_reason && (
        <div style={{ padding: '12px 16px', background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.2)', borderRadius: 10, marginBottom: 12, fontSize: 12, color: 'var(--red)' }}>Rejected: {plan.rejection_reason}</div>
      )}

      {/* Discussion */}
      <Section title="Discussion">
        {(plan.discussion || []).length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No messages yet</div>
        ) : (plan.discussion || []).map(msg => (
          <div key={msg.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
              <span><strong>{msg.user_name}</strong> <span style={{ color: 'var(--text3)' }}>({(msg.user_role || '').replace(/_/g, ' ')})</span></span>
              <span style={{ color: 'var(--text3)' }}>{timeAgo(msg.created_at)}</span>
            </div>
            {msg.message && <div style={{ fontSize: 13, lineHeight: 1.5 }}>{msg.message}</div>}
            {msg.photos?.length > 0 && <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>{msg.photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 4 }} />)}</div>}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea className="form-input" rows={1} placeholder="Type a message..." value={message} onChange={e => setMessage(e.target.value)}
            style={{ flex: 1, resize: 'none', fontSize: 13 }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
          <input type="file" accept="image/*" ref={msgPhotoRef} style={{ display: 'none' }} onChange={async e => { const url = await compressAndUpload(e.target.files[0]); if (url) setMsgPhotos(prev => [...prev, url]); }} />
          <button className="btn btn-secondary btn-sm" onClick={() => msgPhotoRef.current?.click()}>📷</button>
          <button className="btn btn-primary btn-sm" onClick={sendMessage}>Send</button>
        </div>
        {msgPhotos.length > 0 && <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>{msgPhotos.map((p, i) => <img key={i} src={p} alt="" style={{ width: 40, height: 30, objectFit: 'cover', borderRadius: 4 }} />)}</div>}
      </Section>

      {printUnit && <PrintLabelModal unit={printUnit} onClose={() => setPrintUnit(null)} />}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function StepCard({ title, step, total, children }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-body" style={{ padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{title}</div>
        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--green)', borderRadius: 3, transition: 'width 0.3s' }} />
        </div>
        {children}
      </div>
    </div>
  );
}

function PhotoUploader({ photos, onUpload, label }) {
  const fileRef = useRef(null);
  return (
    <div>
      {label && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>}
      <input type="file" accept="image/*" capture="environment" ref={fileRef} style={{ display: 'none' }}
        onChange={e => { onUpload(e.target.files[0]); e.target.value = ''; }} />
      <button className="btn btn-secondary" style={{ width: '100%', marginBottom: 8 }} onClick={() => fileRef.current?.click()}>📷 Take Photo</button>
      {photos.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 4 }} />)}
        </div>
      )}
    </div>
  );
}

function DismantleStep({ partName, index, total, vehicle, onDone, setPrintUnit }) {
  const [photos, setPhotos] = useState([]);
  const [condition, setCondition] = useState('');
  const fileRef = useRef(null);

  const handlePhoto = async (file) => {
    const url = await compressAndUpload(file);
    if (url) setPhotos(prev => [...prev, url]);
  };

  const partsList = DB.get('spareParts') || [];
  const makeCode = (vehicle?.make || 'UNK').substring(0, 3).toUpperCase();
  const d = new Date();
  const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
  const previewSku = `PRT-${makeCode}-${dateCode}-${String(partsList.length + 1).padStart(3, '0')}`;

  return (
    <StepCard title="Step 4: Separate Parts" step={index + 1} total={total}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Part {index + 1} of {total}: {partName}</div>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace', marginBottom: 4 }}>{previewSku}</div>
        <button className="btn btn-secondary btn-sm" onClick={() => setPrintUnit({ sku: previewSku, make: vehicle?.make, model: vehicle?.model, year: vehicle?.year, chassis: vehicle?.chassis, color: vehicle?.color })}>🖨️ Print QR</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Print QR → Stick on part → Photo with QR visible</div>
      <input type="file" accept="image/*" capture="environment" ref={fileRef} style={{ display: 'none' }}
        onChange={e => { handlePhoto(e.target.files[0]); e.target.value = ''; }} />
      <button className="btn btn-secondary" style={{ width: '100%', marginBottom: 8 }} onClick={() => fileRef.current?.click()}>📷 Take Photo</button>
      {photos.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>{photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 4 }} />)}</div>}
      <input className="form-input" style={{ marginBottom: 10 }} value={condition} onChange={e => setCondition(e.target.value)} placeholder="Condition notes (optional)" />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => onDone(partName, [], '', true)}>Skip — Later</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onDone(partName, photos, condition, false)}>✅ Done — Next →</button>
      </div>
    </StepCard>
  );
}

function CompletionSummary({ plan }) {
  const allParts = DB.get('spareParts') || [];
  const childParts = (plan.child_part_ids || []).map(id => allParts.find(p => p.id === id)).filter(Boolean);
  return (
    <Section title="✅ Cutting Complete!">
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Parts from {plan.vehicle_name}:</div>
      {childParts.map(p => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
          <span>✅ {p.sku} — {p.partName || p.category}</span>
          <span style={{ color: 'var(--text3)' }}>{p.status}</span>
        </div>
      ))}
      {(plan.scrap_parts || []).map((s, i) => (
        <div key={i} style={{ padding: '6px 0', fontSize: 12, color: 'var(--text3)' }}>🗑️ {s} — scrapped</div>
      ))}
      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600 }}>Total: {childParts.length} parts created</div>
    </Section>
  );
}


// ═══════════════════════════════════════════════════════════════
// ANALYTICS
// ═══════════════════════════════════════════════════════════════
function CutPlanAnalytics({ plans, onBack }) {
  const allParts = DB.get('spareParts') || [];
  const completed = plans.filter(p => p.status === 'completed');
  const totalPartsCreated = completed.reduce((s, p) => s + (p.total_parts_created || 0), 0);
  const allChildIds = completed.flatMap(p => p.child_part_ids || []);
  const childParts = allChildIds.map(id => allParts.find(p => p.id === id)).filter(Boolean);

  const soldParts = childParts.filter(p => ['sold', 'sold_awaiting_payment', 'delivered'].includes(p.status));
  const availableParts = childParts.filter(p => p.status === 'available');

  const totalVehicleCost = completed.reduce((s, p) => s + (Number(p.parent_purchase_price) || 0), 0);
  const totalCuttingFees = completed.reduce((s, p) => s + (Number(p.cutting_fee) || 0), 0);
  const totalSoldRevenue = soldParts.reduce((s, p) => s + (p.priceJPY || p.costJPY || 0), 0);
  const totalStockValue = availableParts.reduce((s, p) => s + (p.costJPY || 0), 0);

  // Cut type breakdown
  const byType = {};
  completed.forEach(p => {
    const t = p.cut_type || 'Other';
    if (!byType[t]) byType[t] = { count: 0, parts: 0, cost: 0 };
    byType[t].count++;
    byType[t].parts += p.total_parts_created || 0;
    byType[t].cost += Number(p.parent_purchase_price) || 0;
  });

  // Avg days
  const withDays = completed.filter(p => p.started_at && p.completed_at);
  const avgDays = withDays.length > 0 ? Math.round(withDays.reduce((s, p) => s + Math.round((new Date(p.completed_at) - new Date(p.started_at)) / 86400000), 0) / withDays.length) : 0;

  const statStyle = { padding: '16px 14px', background: 'var(--bg3)', borderRadius: 10, textAlign: 'center', border: '1px solid var(--border)' };
  const statVal = { fontSize: 24, fontWeight: 800, marginBottom: 2 };
  const statLabel = { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <h2 style={{ margin: 0 }}>📊 Cut Plan Analytics</h2>
      </div>

      {/* Overview stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={statStyle}><div style={statVal}>{completed.length}</div><div style={statLabel}>Total Cuts</div></div>
        <div style={statStyle}><div style={statVal}>{totalPartsCreated}</div><div style={statLabel}>Parts Created</div></div>
        <div style={statStyle}><div style={statVal}>{completed.length > 0 ? (totalPartsCreated / completed.length).toFixed(1) : 0}</div><div style={statLabel}>Avg Parts/Cut</div></div>
        <div style={statStyle}><div style={statVal}>{avgDays}d</div><div style={statLabel}>Avg Days</div></div>
      </div>

      {/* Revenue */}
      <Section title="Revenue">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Total vehicle cost</span><span style={{ fontWeight: 600 }}>¥{totalVehicleCost.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Cutting fees collected</span><span style={{ fontWeight: 600 }}>${totalCuttingFees.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Sold parts revenue</span><span style={{ fontWeight: 600, color: 'var(--green)' }}>¥{totalSoldRevenue.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Available stock value</span><span style={{ fontWeight: 600 }}>¥{totalStockValue.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
            <span>Parts sold</span><span style={{ fontWeight: 600 }}>{soldParts.length} / {totalPartsCreated}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span>Still available</span><span style={{ fontWeight: 600 }}>{availableParts.length}</span>
          </div>
        </div>
      </Section>

      {/* By cut type */}
      <Section title="By Cut Type">
        {Object.entries(byType).sort((a, b) => b[1].count - a[1].count).map(([type, data]) => (
          <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{type}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{data.parts} parts · ¥{data.cost.toLocaleString()} invested</div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{data.count}</div>
          </div>
        ))}
      </Section>

      {/* Status summary */}
      <Section title="All Plans by Status">
        {Object.entries(STATUS_COLORS).map(([status, st]) => {
          const count = plans.filter(p => p.status === status).length;
          if (count === 0) return null;
          return (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
              <span style={{ padding: '2px 8px', borderRadius: 4, background: st.bg, color: st.color, fontSize: 10, fontWeight: 600, minWidth: 80, textAlign: 'center' }}>{st.label}</span>
              <span style={{ fontWeight: 600 }}>{count}</span>
            </div>
          );
        })}
      </Section>
    </div>
  );
}
